import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import { unwrapData } from "@/lib/api-response";
import { downloadAuthenticatedFile } from "@/lib/download-blob";
import { fileWithSafeUploadName } from "@/lib/upload-filename";
import type {
  Document,
  DocumentDepartmentScope,
  DocumentListResult,
  DocumentPagination,
  ListDocumentsQuery,
  UpdateDocumentPermissionsRequest,
} from "@/types/document";

/** Strapi's governance list reads `page`/`pageSize` (not `limit`); export honours the
 *  same filter set. Shared so the two never drift. */
function toListParams(query?: ListDocumentsQuery) {
  return {
    ...(query?.search ? { search: query.search } : {}),
    ...(query?.departmentId ? { departmentId: query.departmentId } : {}),
    ...(query?.libraryId ? { libraryId: query.libraryId } : {}),
    ...(query?.language ? { language: query.language } : {}),
    ...(query?.ids ? { ids: query.ids } : {}),
  };
}

export const documentService = {
  async getScope(): Promise<DocumentDepartmentScope> {
    const response = await http.get(API_ENDPOINTS.DOCUMENTS.SCOPE);
    return unwrapData<DocumentDepartmentScope>(response);
  },

  async list(query?: ListDocumentsQuery): Promise<DocumentListResult> {
    const params = {
      page: query?.page ?? 1,
      pageSize: query?.limit ?? 25,
      ...toListParams(query),
    };
    const response = await http.get(API_ENDPOINTS.DOCUMENTS.LIST, { params });
    const data = unwrapData<{
      items?: Document[];
      pagination?: DocumentPagination;
    }>(response);
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items,
      pagination: data?.pagination ?? {
        page: 1,
        pageSize: items.length,
        total: items.length,
        pageCount: 1,
      },
    };
  },

  // Accepts the same filter set as `list` so the export mirrors the current view.
  // The backend ignores pagination and streams a real CSV (text/csv) honoring filters.
  async exportCsv(query?: ListDocumentsQuery): Promise<void> {
    await downloadAuthenticatedFile(
      API_ENDPOINTS.DOCUMENTS.EXPORT,
      toListParams(query),
      "documents-export.csv",
    );
  },

  async get(id: string): Promise<Document> {
    const response = await http.get(API_ENDPOINTS.DOCUMENTS.DETAIL(id));
    return unwrapData<Document>(response);
  },

  async upload(
    file: File,
    departmentId: string,
    name?: string,
    libraryId?: string,
    language?: string,
  ): Promise<Document> {
    const formData = new FormData();
    formData.append("file", fileWithSafeUploadName(file));
    if (departmentId) formData.append("departmentId", departmentId);
    if (name) formData.append("name", name);
    if (libraryId) formData.append("libraryId", libraryId);
    if (language) formData.append("language", language);
    const response = await http.upload(
      "POST",
      API_ENDPOINTS.DOCUMENTS.UPLOAD,
      formData,
      { timeout: 120_000 },
    );
    return unwrapData<Document>(response);
  },

  async delete(id: string): Promise<void> {
    await http.delete(API_ENDPOINTS.DOCUMENTS.DETAIL(id));
  },

  async updatePermissions(
    id: string,
    payload: UpdateDocumentPermissionsRequest,
  ): Promise<Document> {
    const response = await http.patch(
      API_ENDPOINTS.DOCUMENTS.PERMISSIONS(id),
      payload,
    );
    return unwrapData<Document>(response);
  },
};
